import {
	Box,
	ButtonBase,
	Divider,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { CheckCheck } from "lucide-react";
import { type FC, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { useNotificationMarkAllAsReadMutation } from "web_myplann/Api/Notifications/UseNotificationMarkAllAsReadMutation";
import { useNotificationsGroupedQuery } from "web_myplann/Api/Notifications/useNotificationsGroupedQuery";
import { NotificationListItem } from "web_myplann/Dashboard/Components/NotificationListItem";
import type { NotificationsTabContext } from "web_myplann/Dashboard/Pages/NotificationsTabsLayout";
import { dedupeById } from "web_myplann/Dashboard/Utils/dedupeById";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import useIntersectionObserver from "web_myplann/Shared/Hooks/useIntersectionObserver";
import { RowSkeletonLoader } from "web_myplann/Shared/RowSkeletonLoader";

export const UnreadNotificationsTab: FC = () => {
	const { t } = useResource();
	const { palette, iconSize } = useTheme();

	const { unreadCount, isCountLoading } =
		useOutletContext<NotificationsTabContext>();

	const {
		data: unreadPages,
		fetchNextPage: fetchNextUnread,
		hasNextPage: hasNextUnread,
		isFetchingNextPage: isFetchingNextUnread,
		isLoading: isUnreadLoading,
	} = useNotificationsGroupedQuery(true);

	const { mutate: markAllAsRead } = useNotificationMarkAllAsReadMutation();

	const unreadNotifications = useMemo(
		() => dedupeById(unreadPages?.pages?.flatMap((p) => p?.data ?? []) ?? []),
		[unreadPages?.pages],
	);

	const newestUnreadId = useMemo(
		() =>
			unreadNotifications.length > 0
				? unreadNotifications.reduce((latest, n) =>
						Date.parse(n.createdAt) > Date.parse(latest.createdAt) ? n : latest,
					).id
				: undefined,
		[unreadNotifications],
	);

	const lastUnreadRef = useIntersectionObserver(
		hasNextUnread && !isFetchingNextUnread
			? () => fetchNextUnread()
			: undefined,
		unreadNotifications.length,
		undefined,
		!!hasNextUnread && !isFetchingNextUnread,
	);

	if (isCountLoading || isUnreadLoading) {
		return <Box pt={1.6} />;
	}

	return (
		<Box pt={1.6}>
			{unreadCount > 0 ? (
				<>
					<Stack display="flex" alignItems="flex-end">
						<ButtonBase
							sx={{
								gap: 0.8,
								pb: 1.6,
							}}
							onClick={() => markAllAsRead(newestUnreadId ?? 0)}
						>
							<CheckCheck size={iconSize.sm} color={palette.primary.main} />
							<Typography variant="body2" color={palette.primary.main}>
								{t(Resources.Notifications.readAll)}
							</Typography>
						</ButtonBase>
					</Stack>
					{unreadNotifications.map((notification) => (
						<Stack key={notification.id}>
							<NotificationListItem {...notification} />
							<Box py={1.6}>
								<Divider />
							</Box>
						</Stack>
					))}
					<Box ref={lastUnreadRef} height={1} />
				</>
			) : (
				<Stack
					height="85vh"
					justifyContent="center"
					alignItems="center"
					gap={1.6}
					padding="0 64px"
				>
					<RowSkeletonLoader disableAnimation />
					<RowSkeletonLoader disableAnimation />
					<RowSkeletonLoader disableAnimation />
					<Typography fontSize={16} fontWeight={600} color={palette.grey[700]}>
						{t(Resources.Activity.List.NoContent)}
					</Typography>
					<Typography
						fontSize={12}
						fontWeight={400}
						color={palette.grey[500]}
						align="center"
					>
						{t(Resources.Notifications.noContent)}
					</Typography>
				</Stack>
			)}
		</Box>
	);
};
